import styled from '@emotion/styled';
import { font, mediaQueries, palette, remCalc, sizeMap } from '../../css';
import { useDefault } from '../../utils';

const limitContentToTwoLines = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        whiteSpace: 'nowrap',
    },
};

const StyledTitle = styled('h3')(
    ({ isActive, _display: display, slideKind, theme }) => {
        const {
            teaser: { title },
        } = useDefault(theme, {
            teaser: {
                title: {
                    lineHeight: 1.1,
                    space: 5,
                    font: {
                        family: font.family.body,
                        weight: font.weight.medium,
                    },
                    textColor: palette('white'),
                },
            },
        });

        const color = `${title.textColor}`;
        const lineHeight = sizeMap.xs * title.lineHeight;
        const basicStyle = {
            margin: 0,
            paddingTop: title.space,
            maxHeight: slideKind === 'SQUARE' ? remCalc(56) : remCalc(lineHeight * 3 + title.space),
            overflow: 'hidden',
            textAlign: display === 'VOD' ? 'center' : 'left',
            fontFamily: title.font.family,
            fontWeight: title.font.weight,
            fontSize: remCalc(sizeMap.xs * 1.05),
            lineHeight: remCalc(lineHeight),
            color,
        };
        switch (display) {
            case 'FULL': {
                return {
                    display: 'block',
                    marginBottom: remCalc(5),
                    color,
                    maxWidth: '90%',
                    fontFamily: font.family.header,
                    fontWeight: font.weight.bold,
                    fontSize: font.size.l,
                    lineHeight: font.size.l,
                    transform: isActive ? 'translateX(0)' : 'translateX(100%)',
                    transitionDuration: '500ms',
                    transitionTimingFunction: 'cubic-bezier(.63, .24, .93, .2)',
                    transitionProperty: 'transform',
                    backfaceVisibility: 'hidden',

                    [mediaQueries.medium]: {
                        maxWidth: '45%',
                    },
                    [mediaQueries.large]: {
                        maxWidth: '45%',
                        fontSize: font.size.xl,
                        lineHeight: font.size.xl,
                    },
                    [mediaQueries.xlarge]: {
                        maxWidth: '42%',
                        fontSize: font.size.xxl,
                        lineHeight: font.size.xxl,
                    },
                };
            }
            case 'OVERLAY': {
                return {
                    ...basicStyle,
                    [mediaQueries.medium]: {
                        padding: 0,
                        maxHeight: 'none',
                        fontFamily: font.family.header,
                        fontWeight: font.weight.bold,
                        fontSize: font.size.m,
                        lineHeight: font.lineHeight.m,
                    },
                    [mediaQueries.xlarge]: {
                        fontSize: font.size.xl,
                        lineHeight: font.lineHeight.xl,
                    },
                };
            }
            case 'MOBILE':
                return {
                    margin: 0,
                    maxHeight: remCalc(100),
                    overflow: 'hidden',
                    fontFamily: font.family.header,
                    fontWeight: font.weight.bold,
                    fontSize: font.size.s,
                    lineHeight: font.lineHeight.s,
                    // color: palette('white'),
                    color,
                };
            case 'TVGUIDE':
                return {
                    // @include clamp(10rem, 5);
                    position: 'relative',
                    display: 'block',
                    maxHeight: remCalc(160),
                    marginBottom: remCalc(5),
                    overflow: 'hidden',
                    fontFamily: font.family.header,
                    fontWeight: font.weight.bold,
                    fontSize: font.size.m,
                    lineHeight: font.lineHeight.m,
                    // color: palette('white'),
                    color,
                    [mediaQueries.large]: {
                        fontSize: remCalc(28),
                        lineHeight: remCalc(28 * 1.1),
                    },
                };
            case 'BIG':
                return {
                    ...basicStyle,
                    [mediaQueries.medium]: {
                        fontSize: font.size.l,
                        lineHeight: font.size.l,
                        maxHeight: remCalc(sizeMap.l * 2 + title.space),
                    },
                };
            case 'SLIDER_TITLE':
                return {
                    ...basicStyle,
                };
            case 'SLIDER_ITEM_TITLE':
                return {
                    ...basicStyle,
                    ...limitContentToTwoLines,
                };
            default:
                return {
                    ...basicStyle,
                    color: color,
                    [mediaQueries.large]: {
                        maxHeight: remCalc(lineHeight * 3 + title.space),
                        overflow: 'hidden',
                    },
                };
        }
    },
    ({ isPlaceholder, display, slideKind, theme }) => {
        const {
            teaser: { bgColor },
        } = useDefault(theme, {
            teaser: {
                bgColor: palette('primary', 'mainNav'),
            },
        });

        if (isPlaceholder) {
            switch (display) {
                case 'OVERLAY':
                    return {
                        marginTop: remCalc(5),
                        paddingTop: 0,
                        width: '80%',
                        height: remCalc(34),
                        background: `linear-gradient(to bottom, ${palette(
                            'primary',
                            'mainNav',
                        )} 45%, transparent 45%, transparent 55%, ${palette('primary', 'mainNav')} 55%)`,
                        color: palette('primary', 'mainNav'),
                        [mediaQueries.medium]: {
                            marginTop: 0,
                            height: remCalc(50),
                            background: `linear-gradient(to bottom, ${palette(
                                'primary',
                                'mainDivider',
                            )} 45%, transparent 45%, transparent 55%, ${palette('primary', 'mainDivider')} 55%)`,
                            color: palette('primary', 'mainDivider'),
                        },
                        [mediaQueries.xlarge]: {
                            height: remCalc(80),
                        },
                    };
                case 'MOBILE':
                    return {
                        width: '80%',
                        height: remCalc(40),
                        background: `linear-gradient(to bottom, ${palette(
                            'primary',
                            'mainDivider',
                        )} 45%, transparent 45%, transparent 55%, ${palette('primary', 'mainDivider')} 55%)`,
                        color: palette('primary', 'mainDivider'),
                    };
                default: {
                    return {
                        marginTop: remCalc(5),
                        paddingTop: 0,
                        width: display === 'BIG' ? '50%' : '80%',
                        height: slideKind === 'SQUARE' ? remCalc(52) : remCalc(34),
                        background:
                            slideKind === 'SQUARE'
                                ? `linear-gradient(to bottom, ${bgColor} 29%, transparent 29%, transparent 36%, ${bgColor} 36%, ${bgColor} 65%, transparent 65%, transparent 73%, ${bgColor} 73%)`
                                : `linear-gradient(to bottom, ${bgColor} 45%, transparent 45%, transparent 55%, ${bgColor} 55%)`,
                        color: bgColor,
                    };
                }
            }
        }
        return {};
    },
);

const StyledSubTitle = styled('h4')(({ theme, _display: display }) => {
    const { global } = useDefault(theme, {
        global: {
            textColor: palette('white'),
        },
    });

    const style = {
        color: global.textColor,
        margin: 0,
        fontFamily: font.family.body,
        fontSize: remCalc(sizeMap.xs * 1.05),
        lineHeight: remCalc(sizeMap.xs * 1.15),
        fontWeight: font.weight.regular,
    };

    switch (display) {
        case 'SLIDER_ITEM_TITLE':
            return {
                ...style,
                fontWeight: font.weight.bold,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...limitContentToTwoLines,
            };
        default:
            return style;
    }
});

export { StyledTitle, StyledSubTitle };
